import React, { CSSProperties, useMemo, useState } from 'react';
import { Calendar } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Colors from '../../../../../utils/colors';
import { CourseLectureEventCalendarTollBar } from './componenets/course-lecture-event-calendar-toll-bar';
import { customMessages } from './consts/calendar-messages';
import globalCourseCalendarStore from '../../../../../stores/global-course-calendar';
import { observer } from 'mobx-react-lite';
import { onNavigate } from './operations/big-calendar-on-navigate';
import { reactBigCalendarLocalizer } from './consts/react-big-calendar-locelazier';
import globalConfigStore from '../../../../../stores/global-config-store';
import globalCourseStore from '../../../../../stores/global-course-store';
import { CalendarDisplayWeekDaysToolbar } from './componenets/calendar-display-week-days-toolbar';
import {
  CreateCalendarScheduleDialog,
  ScheduleType,
} from '../../../../calendar/create-calendar-schedule-dialog';
import { SelectedSlotsState } from '../../../../calendar/tabs/serial/components/time-slots/types/selected-slots';
import { LecturerIdWithActivityTypeId } from '../../../../calendar/tabs/serial/create-lecture-serial-tab';
import { createSerialLecturesOperations } from './operations/create-serial-lectures-operations';
import globalProgramStore from '../../../../../stores/program-store';

// 1. Configure dateFnsLocalizer

export interface CalendarEvent {
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resourceId?: number;
}

const CourseLectureEventCalendar: React.FC = observer(() => {
  //console.log('Resources:', resources);

  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    start: Date;
    end: Date;
    classId: number;
  } | null>();

  const [displayLectureCreationDialog, setDisplayLectureCreationDialog] =
    useState(false);

  const [view, setView] = useState('month');

  const targetBranch = useMemo(
    () => globalConfigStore.rootFilter.currentBranches,
    [globalConfigStore.rootFilter.currentBranches],
  );

  const resources = useMemo(() => {
    return targetBranch?.buildings.flatMap((building) =>
      building.classes.map((classItem) => ({
        id: classItem.id,
        title: classItem.className,
      })),
    );
  }, [targetBranch]);

  // Handle view change
  const onViewChange = (newView) => {
    globalCourseCalendarStore.displayView = newView;
  };

  const currentCourse = globalCourseStore.getCurrentCourse();
  const lectures = currentCourse!.details!.lectures;
  const events: CalendarEvent[] = lectures.map((lecture, index) => ({
    title: lecture.item.lectureName,
    start: new Date(lecture.schedule.startTime),
    end: new Date(lecture.schedule.endTime),
    resourceId: lecture.class.id + index,
  }));

  /*useEffect(() => {
    // TODO remove ?
    globalCourseCalendarStore.events = events;
  });*/

  const handleSelectSlot = ({
    start,
    end,
    resourceId,
  }: {
    start: Date;
    end: Date;
    resourceId: number;
  }) => {
    setSelectedTimeSlot({
      start: start,
      end: end,
      classId: resourceId,
    });
    setDisplayLectureCreationDialog(true);
    console.log(`handleSelectSlot is: ${JSON.stringify(start)}`);
  };

  const handleHideDialog = () => {
    setSelectedTimeSlot(null);
    setDisplayLectureCreationDialog(false);
  };

  const handleViewChange = (view) => {
    const rows = document.querySelectorAll('.rbc-row-content');
    rows.forEach((row) => {
      if (view === 'month') {
        row.style.display = ''; // Keep rows visible for the month view
      } else {
        row.style.display = 'none'; // Hide rows for week and day views
      }
    });
  };

  const handleOnApproveCreateLectures = async (
    selectedSlotsState: SelectedSlotsState,
    lecturesIdsWithActivityTypeId: LecturerIdWithActivityTypeId[],
    setIsLoadingCreateLectures: (loading: boolean) => any,
    onHide: () => any,
    attendanceRequired?: boolean,
  ) => {
    const currentProgramId = globalProgramStore.getCurrentId();
    const currentCourse = globalCourseStore.getCurrentCourse();

    await createSerialLecturesOperations(
      currentProgramId,
      currentCourse?.metadata.id,
      lecturesIdsWithActivityTypeId,
      selectedSlotsState,
      setIsLoadingCreateLectures,
      onHide,
      attendanceRequired,
    );
  };

  return (
    <div style={useStyle.rootComponentStyle}>
      {displayLectureCreationDialog && (
        <CreateCalendarScheduleDialog
          courseName={currentCourse?.metadata.abstractData.courseName ?? ''}
          supportMultiple={false}
          timeSlot={selectedTimeSlot}
          onApprove={handleOnApproveCreateLectures}
          onHide={handleHideDialog}
          scheduleType={ScheduleType.LECTURE}
        />
      )}

      <div>
        <CourseLectureEventCalendarTollBar
          label={globalCourseCalendarStore.date?.toString()}
          onNavigate={(direction, view2) =>
            onNavigate(direction, view2, globalCourseCalendarStore.date)
          }
          onView={onViewChange}
          view={globalCourseCalendarStore.displayView}
        />
      </div>
      <div style={{ marginTop: '8px' }} className={`container ${view}`}>
        <CalendarDisplayWeekDaysToolbar />
        <Calendar
          localizer={reactBigCalendarLocalizer}
          events={globalCourseCalendarStore.events}
          resources={
            globalCourseCalendarStore.displayView === 'day' ? resources : null
          }
          startAccessor="start"
          endAccessor="end"
          selectable
          defaultDate={new Date()} // Add this line
          rtl
          view={globalCourseCalendarStore.displayView} // Use the view state passed from the toolbar
          date={globalCourseCalendarStore.date}
          onView={handleViewChange}
          messages={customMessages}
          components={{
            toolbar: () => <div />,
            resourceHeader: ({ label }) => {
              return <div style={useStyle.style123}>{label}</div>;
            },
          }}
          onSelectSlot={handleSelectSlot}
          dayPropGetter={(event) => ({
            style: {
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: 'white',
            },
          })}
          style={useStyle.style} // Ensure Calendar fills its parent container
          eventPropGetter={(event) => ({
            style: useStyle.eventPropGetterStyle,
          })}
          onSelectEvent={(event) => {
            console.log('Event clicked:', event);
          }}
        />
      </div>
    </div>
  );
});

const useStyle = {
  rootComponentStyle: {
    flex: 1, // Allows stretching
    width: '100%', // Ensures full width
    display: 'flex', // Ensure Flexbox behavior
    flexDirection: 'column', // If needed for internal layout
    overflow: 'hidden', // Prevent overflow issues
  } as CSSProperties,

  style: {
    width: '100%',
    minHeight: '500px',
    maxHeight: '150%',
    marginTop: '4px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255,255,255,0.6)',
    border: `0.25px solid ${Colors.textColor}`,
    currentTime: '#86c5da', // Replace with your desired color
  } as CSSProperties,

  style123: {
    textAlign: 'center',
    fontWeight: 'bold',
    backgroundColor: '#ffffff',
    maxWidth: '120px',
    padding: '10px',
  } as CSSProperties,

  eventPropGetterStyle: {
    backgroundColor: '#86c5da',
    color: 'white',
    borderRadius: '4px',
    border: 'none',
    padding: '2px',
  } as CSSProperties,
};

export default CourseLectureEventCalendar;
